import { useEffect, useRef, useState } from "react"
import { BetSlipFragment } from "../../graphql/core-types"
import { formatCurrency } from "../../helpers/currency"
import { getFundingTotals, getPayoutAmounts } from "../../helpers/bet-line-helpers"
import { WinWagerSummary } from "../bet-credits/bet-credits"
import { useInternalization } from "../../context/cms-context"
import { StakersWon } from "../win-celebration/stakers-won"
import { Side } from "../../helpers/overlay-settings"
import './bet-won-muted.css'

// keep in sync with the transitions in bet-won-muted.css
const ENTER_DELAY_MS = 50
const PANEL_SLIDE_MS = 500
// the count starts once the panel has mostly opened
const COUNT_START_MS = 400
const COUNT_DURATION_MS = 1400
const HOLD_DURATION_MS = 4200
const EXIT_DURATION_MS = PANEL_SLIDE_MS

// fast at first and settling on the final amount, like a tally coming to rest
const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3)

// a quieter win celebration: a panel opens on the screen edge opposite the bet card and counts up the payout
export const BetWonMuted = ({ betSlip, side, onComplete }: { betSlip: BetSlipFragment, side: Side, onComplete: () => void }) => {
  const [isActive, setIsActive] = useState(false)
  const [hasLanded, setHasLanded] = useState(false)
  const amountRef = useRef<HTMLSpanElement>(null)
  const { games, translate } = useInternalization()

  // cash and credits together, like plc-app's settled payout
  const payoutAmounts = getPayoutAmounts(betSlip)
  const payout = payoutAmounts.dollars + payoutAmounts.credits

  useEffect(() => {
    let frame: number | undefined
    let countStartedAt: number | undefined

    // written straight to the dom so the count doesn't re-render the panel every frame
    const tick = (timestamp: number) => {
      countStartedAt ??= timestamp
      const progress = Math.min((timestamp - countStartedAt) / COUNT_DURATION_MS, 1)
      if (amountRef.current) amountRef.current.textContent = formatCurrency(String(payout * easeOutCubic(progress)))
      if (progress < 1) frame = requestAnimationFrame(tick)
      else setHasLanded(true)
    }

    const enterTimeout = setTimeout(() => setIsActive(true), ENTER_DELAY_MS)
    const countTimeout = setTimeout(() => { frame = requestAnimationFrame(tick) }, ENTER_DELAY_MS + COUNT_START_MS)
    const exitTimeout = setTimeout(() => setIsActive(false), HOLD_DURATION_MS)
    const completeTimeout = setTimeout(() => onComplete(), HOLD_DURATION_MS + EXIT_DURATION_MS)
    return () => {
      clearTimeout(enterTimeout)
      clearTimeout(countTimeout)
      clearTimeout(exitTimeout)
      clearTimeout(completeTimeout)
      if (frame !== undefined) cancelAnimationFrame(frame)
    }
    // runs once per win, the parent remounts this with a new key for the next one
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const gameName = betSlip.betItems?.[0]?.gameSlug
  const game = games.find(g => g.systemId === gameName)
  const { plBalance, plCredits } = getFundingTotals(betSlip.betItems || [])

  return (
    <div className={`bet_won_muted__container bet_won_muted__container--${side} ${isActive ? 'bet_won_muted__container--active' : ''}`}>
      <div className={`bet_won_muted__panel ${hasLanded ? 'bet_won_muted__panel--landed' : ''}`}>
        <span className="bet_won_muted__label pl-fontXSContrast">You won</span>
        <span ref={amountRef} className="bet_won_muted__amount pl-fontXL">{formatCurrency('0')}</span>
        <WinWagerSummary
          className="bet_won_muted__subtext pl-fontXS"
          gameLabel={translate(`${game?.displayName || gameName}`) || null}
          plBalance={plBalance}
          plCredits={plCredits}
          creditsPaidOut={payoutAmounts.credits}
        />
        <StakersWon betSlip={betSlip} className="bet_won_muted__stakers pl-fontXSContrast" />
      </div>
    </div>
  )
}
